'use client'

import BasicMdParser from '@trustoo/components/ui/BasicMdParser'
import FixedWarningPopup from '@trustoo/components/ui/FixedWarningPopup'
import type { ToastState } from '@trustoo/components/ui/toaster/types/toastTypes'
import { twMerge } from 'tailwind-merge'

export interface ToastListProps {
    toasts: ToastState
    /** Called once a toast has finished animating out, to drop it from the store. */
    onToastExited: (id: string) => void
    /** `id` of the stack container. */
    id?: string
    /** Extra classes for the stack container. */
    className?: string
    /** Extra classes for each popup. */
    popupClassName?: string
}

/**
 * Renders a toast store as a stack of `FixedWarningPopup`s, newest first. Both toast channels go
 * through this, so a modal's own toasts are indistinguishable from the app toaster's.
 *
 * `FixedWarningPopup` positions itself (`fixed`, or `absolute` above `md`), which is wrong for a
 * stack: the container places the popups, so each one is reset to `relative` here. Renders nothing
 * when there are no toasts, so an in-flow container takes up no space.
 *
 * Each popup carries its own ARIA role rather than the stack being one live region, because the
 * container is mounted along with its first toast and a live region only announces what arrives
 * after it.
 */
const ToastList = ({ toasts, onToastExited, id, className, popupClassName }: ToastListProps) => {
    const entries = Object.values(toasts).reverse()

    if (entries.length === 0) {
        return null
    }

    return (
        <div id={id} className={twMerge('flex flex-col gap-2 overflow-hidden', className)}>
            {entries.map(({ id, msg, className: toastClassName, restartKey, ...options }) => (
                <FixedWarningPopup
                    key={id}
                    role={options.red ? 'alert' : 'status'}
                    className={twMerge(
                        'pointer-events-auto relative max-w-none [overflow-wrap:anywhere] max-md:relative',
                        popupClassName,
                        toastClassName,
                    )}
                    show
                    restartKey={restartKey}
                    onExited={() => onToastExited(id)}
                    {...options}>
                    <BasicMdParser input={msg} disallowLinks />
                </FixedWarningPopup>
            ))}
        </div>
    )
}

export default ToastList
